import { useEffect, useState } from "react";
import axios from "axios";
import {
  Send, MousePointer2, AlertTriangle, CheckCircle2, MinusCircle,
  TrendingUp, RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid,
} from "recharts";
import { API } from "../../config";

/**
 * SmsWelcomeEngagementWidget
 *
 * Surfaces the % of welcome SMS recipients that actually clicked the
 * embedded short-link (i.e. opened the wallet page). This is a direct
 * proxy for the onboarding conversion rate of the SMS-driven flow.
 *
 * Backend: GET /api/admin/sms-welcome-engagement?days=30
 */

const STATUS_THEME = {
  good:     { text: "text-green-300",  border: "border-green-500/30",  bg: "bg-green-500/5",   Icon: CheckCircle2, label: "Conversione ottima (≥70%)" },
  warning:  { text: "text-amber-300",  border: "border-amber-500/30",  bg: "bg-amber-500/5",   Icon: AlertTriangle, label: "Sotto soglia (40–69%)" },
  critical: { text: "text-red-300",    border: "border-red-500/30",    bg: "bg-red-500/5",     Icon: AlertTriangle, label: "Conversione critica (<40%)" },
  neutral:  { text: "text-gray-400",   border: "border-gray-700",      bg: "bg-black/30",      Icon: MinusCircle,  label: "Campione insufficiente (<5 SMS)" },
};

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

export const SmsWelcomeEngagementWidget = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem("lojack_token") || "";
      const r = await axios.get(`${API}/admin/sms-welcome-engagement?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  if (loading) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="sms-engagement-loading">
        <p className="text-gray-500 text-sm flex items-center gap-2">
          <RefreshCw size={14} className="animate-spin" /> Carico engagement SMS welcome…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-[#111] border border-red-500/30 rounded-2xl p-5" data-testid="sms-engagement-error">
        <p className="text-red-300 text-sm">{error || "Dati non disponibili"}</p>
      </div>
    );
  }

  const theme = STATUS_THEME[data.status] || STATUS_THEME.neutral;
  const StatusIcon = theme.Icon;
  const notClickedCount = Math.max(0, (data.total_sent || 0) - (data.clicked || 0));

  return (
    <div className={`bg-[#111] border ${theme.border} rounded-2xl p-5 space-y-4`} data-testid="sms-engagement-widget">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Send size={16} className="text-lojack-blue-400" />
            SMS Welcome Engagement
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">Ultimi {data.window_days} giorni · Proxy della conversion rate del flusso SMS</p>
        </div>
        <button
          onClick={fetchData}
          className="text-gray-500 hover:text-gray-300 p-1"
          aria-label="Aggiorna"
          data-testid="sms-engagement-refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Primary metric */}
      <div className={`${theme.bg} rounded-xl p-4 flex items-center gap-4`}>
        <div className={`w-12 h-12 rounded-full ${theme.bg} flex items-center justify-center border ${theme.border}`}>
          <StatusIcon size={22} className={theme.text} />
        </div>
        <div className="flex-1">
          <p className="text-gray-400 text-xs uppercase tracking-wider">Conversion rate</p>
          <p className={`text-3xl font-bold ${theme.text}`} data-testid="sms-engagement-conversion-rate">
            {pct(data.conversion_rate)}
          </p>
          <p className="text-gray-500 text-xs mt-0.5">{theme.label}</p>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Inviati" value={data.total_sent} icon={Send} testId="sms-engagement-sent" />
        <Stat label="Cliccati" value={data.clicked} icon={MousePointer2} testId="sms-engagement-clicked" accent={data.clicked > 0 ? "text-green-300" : "text-gray-500"} />
        <Stat label="Non cliccati" value={notClickedCount} icon={MinusCircle} testId="sms-engagement-unclicked" accent={notClickedCount > 0 ? "text-amber-300" : "text-gray-500"} />
      </div>

      {/* Daily series chart */}
      {data.daily_series && data.daily_series.length > 0 && (
        <div className="pt-2" data-testid="sms-engagement-chart">
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={data.daily_series} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#555"
                fontSize={10}
                tickFormatter={(d) => d.slice(5)}  /* MM-DD */
              />
              <YAxis stroke="#555" fontSize={10} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0a0a0a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#aaa" }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="sent" name="Inviati" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="clicked" name="Cliccati" fill="#22c55e" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Footer hint when below 70% */}
      {data.status === "warning" || data.status === "critical" ? (
        <div className={`text-xs ${theme.text} flex items-start gap-2 p-2 rounded ${theme.bg} border ${theme.border}`}>
          <TrendingUp size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Conversione sotto soglia ideale (70%). Possibili cause: SMS troncato (link troppo lungo), numero sbagliato, template
            poco chiaro, oppure tempo di risposta cliente alto. Cumulativo lifetime: <b>{data.lifetime_total}</b> SMS welcome.
          </span>
        </div>
      ) : null}
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, testId, accent = "text-white" }) => (
  <div className="bg-black/40 border border-gray-800 rounded-lg p-3" data-testid={testId}>
    <p className="text-gray-500 text-[10px] uppercase tracking-wider flex items-center gap-1">
      <Icon size={11} /> {label}
    </p>
    <p className={`${accent} text-xl font-bold mt-1`}>{value ?? 0}</p>
  </div>
);

export default SmsWelcomeEngagementWidget;
